import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../Utils/SnackBarContext';

/**
 * useCaseDelete Hook
 * 
 * Custom hook that encapsulates case deletion logic following DRY principles.
 * Handles dialog state, WebSocket communication, and user feedback.
 * 
 * Features:
 * - Manages delete dialog state
 * - Sends delete command via WebSocket
 * - Shows appropriate snackbar messages
 * - Optional navigation after deletion
 * - Optional custom callback after successful deletion
 * 
 * @param {object} websocketConnection - Object containing sendMessage and isConnected
 * @param {function} onDeleteSuccess - Optional callback after successful deletion
 * @param {boolean} navigateOnDelete - Whether to navigate to home after deleting current case
 * @param {string} currentCaseId - Current case ID (for navigation check)
 * 
 * @returns {object} Object containing dialog state and handlers
 */
export const useCaseDelete = ({
  sendMessage,
  isConnected,
  onDeleteSuccess = null,
  navigateOnDelete = false,
  currentCaseId = null
}) => {
  const { t } = useTranslation();
  const { openSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Opens the delete confirmation dialog for a specific case
   */
  const openDeleteDialog = useCallback((caseData) => {
    console.log('Opening delete dialog for case:', caseData);
    setCaseToDelete(caseData);
    setDeleteDialogOpen(true);
  }, []);

  /**
   * Closes the delete dialog and clears state
   */
  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setCaseToDelete(null);
  }, []);

  /**
   * Confirms and executes the case deletion
   */
  const confirmDelete = useCallback(() => {
    console.log('Delete confirmed for case:', caseToDelete);

    if (!caseToDelete) {
      console.log('No case selected for delete');
      return;
    }

    if (!sendMessage || !isConnected) {
      console.log('WebSocket not available or not connected');
      openSnackbar(
        t('sidebar.delete_case_error_connection') || 'Connection error. Please try again.',
        3000,
        'error'
      );
      return;
    }

    try {
      // prevent duplicate deletes
      if (isDeleting) {
        console.log('Delete already in progress');
        return;
      }
      setIsDeleting(true);
      const message = {
        command: 'deletecase',
        caseid: caseToDelete.id
      };

      console.log('Sending WebSocket delete message:', message);
      sendMessage(message);
      console.log('Delete message sent, waiting for server response');

      // Keep the dialog open until we receive the server response. We will close
      // it in handleDeleteResponse. This allows us to show a loading state.

      // Execute custom success callback if provided
      // Note: This is called optimistically. The actual success handling
      // should be done in the WebSocket message subscription
      if (onDeleteSuccess) {
        onDeleteSuccess(caseToDelete);
      }

    } catch (error) {
      console.error('Error deleting case:', error);
      openSnackbar(
        t('sidebar.delete_case_error') || 'Failed to delete case',
        3000,
        'error'
      );
    }
  }, [caseToDelete, sendMessage, isConnected, openSnackbar, t, closeDeleteDialog, onDeleteSuccess, isDeleting]);

  /**
   * Handles the delete response from the server
   * Should be called from the WebSocket message subscription
   */
  const handleDeleteResponse = useCallback((success, deletedCaseId = null) => {
    // Reset loading state regardless of response
    setIsDeleting(false);
    if (success) {
      openSnackbar(
        t('sidebar.delete_case_success') || 'Case deleted successfully',
        3000,
        'success'
      );

      console.log('handleDeleteResponse: checking navigation. navigateOnDelete:', navigateOnDelete, 'currentCaseId:', currentCaseId, 'deletedCaseId:', deletedCaseId);
      // Navigate to home if we deleted the current case and navigation is enabled
      // Cast to string to ensure loose comparison (e.g. "123" vs 123)
      if (navigateOnDelete && currentCaseId && String(deletedCaseId) === String(currentCaseId)) {
        console.log('Navigating to home...');
        navigate('/');
      }
    } else {
      openSnackbar(
        t('sidebar.delete_case_error') || 'Failed to delete case',
        3000,
        'error'
      );
    }
    // Close the dialog after response
    closeDeleteDialog();
  }, [openSnackbar, t, navigate, navigateOnDelete, currentCaseId, closeDeleteDialog]);

  return {
    deleteDialogOpen,
    caseToDelete,
    openDeleteDialog,
    closeDeleteDialog,
    confirmDelete,
    isDeleting,
    handleDeleteResponse
  };
};
